import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Key, Copy, Check, RotateCcw, Search, ShoppingBag, ChevronDown, AlertCircle, ArrowLeft, LogOut } from 'react-feather';
import { toast } from 'react-toastify';
import config from '../../../config';
import CustomizerContext from '../../../_helper/Customizer';
import './apikey-view.css';

const APIKeyView = () => {
    const { t } = useTranslation();
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm();

    const navigate = useNavigate();
    const { layoutURL } = useContext(CustomizerContext);
    const token = localStorage.getItem('sessiontoken');
    const configheader = { headers: { Authorization: `${token}` } };

    const handleBack = () => {
        const layout = layoutURL || localStorage.getItem('sidebar_layout') || 'compact-wrapper';
        navigate(`${process.env.PUBLIC_URL}/admin/home/${layout}`);
    };

    const handleLogout = () => {
        localStorage.clear();
        window.location.href = `${process.env.PUBLIC_URL}/login`;
    };

    const [concessionaires, setConcessionaires] = useState([]);
    const [concessionaireId, setConcessionaireId] = useState('');
    const [tenantCode, setTenantCode] = useState('');
    const [apiKeyResult, setApiKeyResult] = useState(null);
    const [copied, setCopied] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        axios
            .get(`${config.hostname}/concessionaire`, configheader)
            .then((response) => setConcessionaires(response.data || []))
            .catch((error) => console.error(error));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const selectedMall = useMemo(
        () => concessionaires.find((c) => c._id === concessionaireId),
        [concessionaires, concessionaireId],
    );

    const handleReset = () => {
        setConcessionaireId('');
        setTenantCode('');
        setApiKeyResult(null);
        setCopied(false);
    };

    const handleViewApiKey = async () => {
        setSubmitting(true);
        try {
            const response = await axios.post(
                `${config.hostname}/tenant/getapikeybytcode`,
                { concessionaire_id: concessionaireId, tenant_code: tenantCode },
                configheader,
            );
            setApiKeyResult(response.data);
        } catch (error) {
            if (error.response && error.response.status === 404) {
                toast.error(error.response.data.message || t('API key not found'));
            } else {
                console.error('Error fetching API key:', error);
                toast.error(error.message || t('An error occurred'));
            }
            setApiKeyResult(null);
        } finally {
            setSubmitting(false);
        }
    };

    const handleCopy = () => {
        if (!apiKeyResult?.tenant_api_key) return;
        navigator.clipboard.writeText(apiKeyResult.tenant_api_key);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const formValid = concessionaireId && tenantCode.trim();

    return (
        <div className="v3 v3-apikey-view">
            <header className="v3-apik-hero">
                <div className="v3-apik-hero-icon" aria-hidden>
                    <Key size={20} />
                </div>
                <div className="v3-apik-hero-text">
                    <div className="v3-label">{t('Backend tools · API access')}</div>
                    <h1 className="v3-display v3-apik-title">{t('API key lookup')}</h1>
                    <p className="v3-body v3-apik-sub">
                        {t("Find a tenant's API key by mall and tenant code.")}
                    </p>
                </div>
                <div className="v3-apik-hero-actions">
                    <button type="button" className="v3-btn v3-btn-ghost" onClick={handleBack}>
                        <ArrowLeft size={14} />
                        {t('Back to malls')}
                    </button>
                    <button type="button" className="v3-btn v3-apik-logout" onClick={handleLogout}>
                        <LogOut size={14} />
                        {t('Logout')}
                    </button>
                </div>
            </header>

            <section className="v3-card v3-apik-form-card">
                <form onSubmit={handleSubmit(handleViewApiKey)} className="v3-apik-form" autoComplete="off">
                    <div className="v3-apik-grid">
                        <div className="v3-field">
                            <label className="v3-label" htmlFor="concessionaire">{t('Mall')}</label>
                            <div className="v3-select-wrap">
                                <select
                                    id="concessionaire"
                                    className="v3-input v3-select"
                                    value={concessionaireId}
                                    onChange={(e) => {
                                        setConcessionaireId(e.target.value);
                                        setApiKeyResult(null);
                                    }}
                                    required
                                >
                                    <option value="">{t('Select a mall…')}</option>
                                    {concessionaires.map((c) => (
                                        <option key={c._id} value={c._id}>{c.concessionaire_name}</option>
                                    ))}
                                </select>
                                <ChevronDown size={14} className="v3-select-caret" aria-hidden />
                            </div>
                        </div>

                        <div className="v3-field">
                            <label className="v3-label" htmlFor="tenant_code">{t('Tenant code')}</label>
                            <input
                                id="tenant_code"
                                type="text"
                                className={`v3-input ${errors.tenant_code ? 'v3-input-error' : ''}`}
                                value={tenantCode}
                                placeholder={t('e.g. T-0042')}
                                onChange={(e) => {
                                    setTenantCode(e.target.value);
                                    setApiKeyResult(null);
                                }}
                                required
                            />
                            {errors.tenant_code && (
                                <span className="v3-field-error">
                                    <AlertCircle size={11} /> {t('Tenant code is required')}
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="v3-apik-actions">
                        <button
                            type="submit"
                            className="v3-btn v3-btn-primary"
                            disabled={!formValid || submitting}
                        >
                            <Search size={14} />
                            {submitting ? t('Looking up…') : t('View API key')}
                        </button>
                        <button type="button" className="v3-btn v3-btn-ghost" onClick={handleReset}>
                            <RotateCcw size={14} />
                            {t('Reset')}
                        </button>
                    </div>
                </form>
            </section>

            {apiKeyResult && (
                <section className="v3-card v3-apik-result">
                    <div className="v3-apik-result-header">
                        <span className="v3-ai-tag">{t('API KEY')}</span>
                        {selectedMall && (
                            <span className="v3-apik-result-mall">{selectedMall.concessionaire_name}</span>
                        )}
                    </div>

                    <div className="v3-apik-key-row">
                        <code className="v3-apik-key">{apiKeyResult.tenant_api_key}</code>
                        <button
                            type="button"
                            className={`v3-btn v3-apik-copy ${copied ? 'is-copied' : ''}`}
                            onClick={handleCopy}
                            aria-label={copied ? t('Copied') : t('Copy API key')}
                        >
                            {copied ? <Check size={14} /> : <Copy size={14} />}
                            {copied ? t('Copied') : t('Copy')}
                        </button>
                    </div>

                    {Array.isArray(apiKeyResult.tenants) && apiKeyResult.tenants.length > 0 && (
                        <div className="v3-apik-tenants">
                            <div className="v3-label v3-apik-tenants-label">
                                {t('Tenants using this key · {{count}}', { count: apiKeyResult.tenants.length })}
                            </div>
                            <ul className="v3-apik-tenants-list">
                                {apiKeyResult.tenants.map((t) => (
                                    <li key={t.tenant_code} className="v3-apik-tenant">
                                        <div className="v3-apik-tenant-icon" aria-hidden>
                                            <ShoppingBag size={14} />
                                        </div>
                                        <span className="v3-apik-tenant-name">{t.tenant_store_name}</span>
                                        <code className="v3-apik-tenant-code">{t.tenant_code}</code>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </section>
            )}
        </div>
    );
};

export default APIKeyView;
